// 라이브 하늘 → unlit 커스텀 셰이더용 공유 조명 uniform.
// ───────────────────────────────────────────────────────────────────────────
// 배경: 지형(HeightfieldPoC)·강(WorldRivers)은 씬 라이트를 안 받는 커스텀 셰이더(unlit)라,
// ProceduralSky가 밤으로 조명을 돌려도 바닥만 대낮처럼 밝게 남는 버그가 있었다.
// 해법: 모든 해당 머티리얼이 "같은 uniform 객체"를 참조 → ProceduralSky가 이 value 하나만
// 매 프레임 mutate하면 타일 수와 무관하게 전부 갱신(드로콜/리렌더 영향 0).
//
// HDRI(정적) 모드에선 항상 흰색(1,1,1) — 기존 외관 완전 불변. ProceduralSky 언마운트 시 리셋.
import * as THREE from 'three';

// 하늘 틴트 — unlit 셰이더 최종색에 곱해짐. 낮=흰색, 밤=달빛 남색, 황혼=주황.
export const SKY_TINT = { value: new THREE.Color(1, 1, 1) };

// 밤 정도 0(낮)~1(깊은 밤) — 등불(NightLanterns) 점등 강도 등 밤 전용 연출 구동용.
export const SKY_NIGHT = { value: 0 };

// ── 태양 공유 uniform ────────────────────────
// 왜 필요한가: 태양 방향은 ProceduralSky 의 **내부** uniform 이라 다른 셰이더가 볼 수 없었다.
// 그래서 수면(WATER_FRAG)은 vec3(0.4,0.9,0.3) 하드코딩 광원을 쓰고 있었고, 황혼에 해가 서쪽인데
// 하이라이트는 딴 데를 봤다 — **원경 물의 1순위 단서인 윤슬**이 원리적으로 불가능했다.
// SKY_TINT 와 같은 방식(공유 객체를 ProceduralSky 가 매 프레임 mutate)이라 참조 머티리얼이
// 몇 개든 드로콜/리렌더 영향 0. HDRI(정적) 모드에선 resetSkyLight 가 낮 기본값으로 되돌린다.
export const SKY_SUN_DIR = { value: new THREE.Vector3(0.4, 0.9, 0.3).normalize() };
export const SKY_SUN_COLOR = { value: new THREE.Color(1, 0.96, 0.88) };
// 태양 지평선 위 가시도 0~1 — 밤엔 0 이라 윤슬·스펙큘러가 자동으로 꺼진다.
export const SKY_SUN_VIS = { value: 1 };

// ── 대기(안개·구름 그림자) 공유 uniform ──
// AtmosphereController가 매 프레임 mutate — HF 지형·강물 등 unlit 커스텀 셰이더가 같은 참조를 씀.
// (일반 머티리얼의 안개는 scene.fog가 전담, 아래는 scene.fog가 안 먹는 커스텀 셰이더용 미러값.)
export const ATMO_FOG_COLOR = { value: new THREE.Color('#cfe0ee') }; // 하늘 지평선색에서 파생(고정색 아님)
export const ATMO_FOG_NEAR = { value: 350 };
export const ATMO_FOG_FAR = { value: 800 };
export const ATMO_FOG_ON = { value: 0 };            // 0|1 — 설정 fogEnabled와 동기
// 원경 지형의 안개 끝(m). 0 = 원경 없음. FarTerrain 이 쓰고 AtmosphereController 가 읽어
// 근경 안개를 이 끝까지 한 곡선으로 늘린다 — 근경 끝에서 100% 로 막으면 그 너머 원경이 다시 보여 흰 띠가 된다.
export const ATMO_FAR_END = { value: 0 };
// ── 그 자리의 «공기» — 하늘색 위에 얹는 지역 색 ──
// 바이옴(동 단위)·역세권 구역이 여기에 목표색과 얹는 양을 남긴다. 안개색을 스스로 계산하는
// 소비처(FarTerrain 은 자기 식으로 낮·밤을 푼다)가 그 «차이분»만 따라오게 하는 자리다.
// AMT 가 0 이면 그 소비처는 종전과 픽셀 하나 안 다르다.
export const ATMO_AIR_COLOR = { value: new THREE.Color('#cfe0ee') };
export const ATMO_AIR_AMT = { value: 0 };

// ── 안개의 **기준점** — 카메라가 아니라 플레이어 ────────────────────
// [무엇이 틀려 있었나] 안개 거리를 `distance(vWorld, cameraPosition)` 로 재고 있었다.
//   3인칭 줌아웃에서 카메라는 플레이어 **수백 m 뒤/위**에 선다. 그러면 발밑 지면이
//   이미 안개 끝에 가까워져, 「안개 끝 1,400m」로 두고 줌아웃하면 세계가 통째로
//   하얀 원반이 된다(실사고 캡처 2026-08-22). 안개는 「내가 어디까지 보이나」이지
//   「카메라가 어디 떠 있나」가 아니다.
// [원통형인 이유] xz 만 잰다(y 무시). 구형으로 재면 산 위나 부감에서 **발밑 땅이
//   안개에 잠긴다** — 마인크래프트가 1.18.1 에서 구형→원통형으로 바꾼 이유가 그것이고,
//   거기서도 렌더 거리의 92% 지점부터 안개가 시작해 **청크 경계를 안개로 덮는다.**
//   우리 이음매(근경 지형 반경) 문제도 같은 처방이다 — 지우지 말고 덮는다.
// [공유 참조] 값 하나만 mutate 하면 지형·물·원경이 같은 프레임에 따라온다.
//   CameraRig 가 cameraFocus 를 쓸 때 AtmosphereController 가 여기로 옮겨 담는다.
export const ATMO_FOCUS = { value: new THREE.Vector2(0, 0) };
// x=그림자 강도(0=끔, 밤엔 자동 감쇠), y=구름 커버리지(skyStore.cloudCover)
export const ATMO_CLOUD = { value: new THREE.Vector2(0, 0.35) };
// 구름 드리프트 시계 — ProceduralSky 하늘 구름과 지면 그림자가 같은 위상을 쓰게 하는 단일 시계
export const CLOUD_TIME = { value: 0 };

// 구름 디테일 품질 0=낮음 / 1=보통 / 2=높음.
// ProceduralSky(하늘 구름)와 HeightfieldPoC(지면 구름그림자)가 "같은 값"을 봐야
// 구름 밑=그늘 픽셀 정합이 유지된다 — 그래서 설정 prop이 아니라 공유 uniform으로 둔다.
//   0: fbm 4옥타브 1회 (기존과 동일 비용) — 엣지 폭만 좁혀 실루엣 확보(공짜)
//   1: + 태양방향 1탭 방향광(fbm 4) + 권운(fbm2 2) = 하늘 10옥타브 / 지면 4옥타브
//   2: + 도메인 워프 2축(fbm2 ×2) = 하늘 14옥타브 / 지면 8옥타브(지면도 워프해야 정합)
export const CLOUD_DETAIL = { value: 1 };

// 라이브 하늘의 현재 지평선색 — ProceduralSky가 매 프레임 기록, 안개색 파생용(HDRI 모드에선 미사용)
export const SKY_HORIZON = { value: new THREE.Color('#cde4ef') };

// ── 그늘색 ────────────────────────────────────────────────────
// 「그림자는 덜 밝은 곳이 아니라 하늘빛만 받는 곳이다.」
// 종전 지면 그림자는 `col *= sh` 무채색 감산이라, 그늘이 그냥 회색으로 내려앉았다.
// 실제 야외광에서 그늘은 태양 직사광이 빠지고 **천공광(하늘)만** 남는 자리라 푸르게
// 물든다 — 레퍼런스(하이테일/마크 셰이더)의 「그늘이 청록」이 전부 이 한 가지다.
// 낮에만 천정색 쪽으로 당긴다(밤엔 천정이 거의 검정이라 섞으면 캄캄해진다).
// 흰색(1,1,1) = 종전과 픽셀 단위로 동일 — 그래서 회귀가 없다.
export const SKY_SHADE = { value: new THREE.Color(1, 1, 1) };

// ── 밤 연출 공유 uniform ────────────────────────────────────────
// [왜 새로 파나] 여태 밤을 조절하는 축이 「밤 밝기(nightLift)」 하나였다. 그 값은 밤 팔레트를
//   통째로 보간해서 **환경광·지면 틴트·하늘색을 같은 비율로** 올린다 — 즉 「전부 같이 밝아진다」.
//   밝기를 올리면 등불이 이길 상대가 사라지고, 내리면 아무것도 안 보인다. 밝기 축 하나로는
//   원리적으로 「어두운데 읽히는 밤」에 못 간다. 밤도 낮과 같이 **밝기 / 대비 / 색**을 갈라야 한다.
//   (낮은 2026-08-10 에 이미 lightContrast·shadeTint 로 갈라 놨다 — 밤만 안 갈려 있었다.)

// 달빛 가시도 0~1 — 밤 정도 × 「밤 대비」. 수면 윤슬(WorldRivers)이 이걸로 달길을 그린다.
// uSunVis 는 밤에 정의상 0 이라(태양이 지평선 아래) 물의 반짝임이 **원리적으로 불가능**했다.
export const SKY_MOON_VIS = { value: 0 };
// 달빛색 — 태양색(SKY_SUN_COLOR)과 갈라 둔다. 밤에 uSunColor 를 쓰면 노을 잔광이 물에 남는다.
export const SKY_MOON_COLOR = { value: new THREE.Color(0.66, 0.76, 1.0) };
// 밤 색 분리 계수 0~1 — 지형 셰이더가 밤 틴트를 자기 휘도 쪽으로 얼마나 당길지.
// 종전엔 HF_FRAG 안에 0.5 로 박혀 있었다. 유니폼으로 빼야 손잡이가 된다.
export const SKY_NIGHT_SEP = { value: 0.5 };

export function resetSkyLight() {
  SKY_TINT.value.setRGB(1, 1, 1);
  SKY_NIGHT.value = 0;
  SKY_SUN_DIR.value.set(0.4, 0.9, 0.3).normalize();
  SKY_SUN_COLOR.value.setRGB(1, 0.96, 0.88);
  SKY_SUN_VIS.value = 1;
  SKY_SHADE.value.setRGB(1, 1, 1);
  SKY_MOON_VIS.value = 0;
  SKY_NIGHT_SEP.value = 0.5;
}
